import React, { useState, useEffect, useContext } from 'react';
import LeadershipStyleImg from "../../../../Assets/Images/LeadershipStyle.png";
import PolarAreaChart from "../../../PolarAreaChart/PolarAreaChart";
import LeadershipStyleCollabrativeGroup from "../groupReport-pdf/LeadershipStyleCollabrativeGroup";
import LeadershipFlow2 from './LeadershipFlow2';
import HeaderPreview from "../../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../../GeneratePdf/FooterPreview";
import GroupReportContext from '../../../../Context/GroupReportContext';
import { initialDataLeaderShip } from '../groupReport-pdf/QuadrantInFocus';
import { applyFullTieOrder, isFullTie } from '../../../../utils/quadrantTieOrder';

export default function LeadershipChart({ apiData, pageNumber }) {
  const [leadershipSnapshotResults, setLeadershipSnapshotResults] = useState([]);
  const [leadershipLabels, setLeadershipLabels] = useState([]);
  const [mostSelecTedStarter, setMostSelecTedStarter] = useState('');
  const [highestGroup, setHighestGroup] = useState("collaborative");
  const groupReportCtx = useContext(GroupReportContext);
  const [dynamicData, setDynamicData] = useState([...initialDataLeaderShip]);

  // ✅ NEW STATE for allResults
  const [allResults, setAllResults] = useState([]);

  const quadrantScores = {
    "PERFORMANCE": 1,
    "STRATEGIC": 2,
    "VISIONARY": 3,
    "COLLABORATIVE": 4
  };

  const displayData = dynamicData?.filter(
    (el) => el.quadrant === mostSelecTedStarter?.toUpperCase()
  );

  useEffect(() => {
    const performanceObj = groupReportCtx?.averagePercentagesLeadershipSelf?.find((el) => el?.name == "Performance");
    const strategiceObj = groupReportCtx?.averagePercentagesLeadershipSelf?.find((el) => el?.name == "Strategic");
    const visionaryObj = groupReportCtx?.averagePercentagesLeadershipSelf?.find((el) => el?.name == "Visionary");
    const collaborativeObj = groupReportCtx?.averagePercentagesLeadershipSelf?.find((el) => el?.name == "Collaborative");

    const avgPercentSelf = [  strategiceObj?.avg, visionaryObj?.avg,   performanceObj?.avg,  collaborativeObj?.avg,  ]
    const selfLeadership = groupReportCtx?.averagePercentagesLeadershipSelf;
    // 4-way tie: first style in clockwise order (Performance) wins
    const maxAvgObj = isFullTie(selfLeadership?.map((el) => el?.avg))
      ? applyFullTieOrder(selfLeadership, (el) => el?.name, (el) => el?.avg)[0]
      : selfLeadership?.reduce((max, current) => {
        return current.avg > max.avg ? current : max;
      });

    setLeadershipSnapshotResults(avgPercentSelf);
    setLeadershipLabels(["strategic", "visionary", "performance", "collaborative"]);
    setMostSelecTedStarter(maxAvgObj?.name?.toLowerCase());
  }, [apiData, groupReportCtx?.averagePercentagesLeadershipSelf]);

  // ✅ Calculate allResults and store in state
  useEffect(() => {
    const allVariableSets = [apiData?.variables];
    const results = [];

    const filteredData = dynamicData?.filter(
      (el) => el.quadrant === mostSelecTedStarter?.toUpperCase()
    );

    filteredData?.forEach((el, index) => {
      const res = groupReportCtx?.calculateQuestionScoreLeadership(
        quadrantScores?.[el.quadrant],
        allVariableSets,
        index + 1,
        "in_focus"
      );

      if (
        res?.averageScore !== null &&
        quadrantScores?.[el.quadrant] === quadrantScores?.[mostSelecTedStarter?.toUpperCase()]
      ) {
        results.push({
          ...res,
          question: el.question
        });
      }
    });

    setAllResults(results);
  }, [apiData, mostSelecTedStarter, dynamicData, groupReportCtx]);

  const getTop3Questions = () => {
    const sortedAllresults = [...allResults]
      ?.sort((a, b) => b.averageScore - a.averageScore)
      ?.filter((el) => el.averageScore >= 0);
    return sortedAllresults?.slice(0, 3);
  };

  const getLastFilledIndex = (scores) => {
    for (let i = scores.length - 1; i >= 0; i--) {
      if (scores[i] === 1) return i;
    }
    return -1;
  };

  const renderRectangle = (score, index, scores) => {
    const lastFilledIndex = getLastFilledIndex(scores);
    return index <= lastFilledIndex ? <div></div> : null;
  };

  const getScores = (questionNumber, targetScore) => {
    if (!apiData?.variables) return [0, 0, 0, 0];

    const standardNum = questionNumber;
    const offsetNum = questionNumber + 10;

    const variableNames = [
      `ls${standardNum}_self_pass2`,
      `ls${offsetNum}_self_pass2`,
      `ls${offsetNum}_self_pass1`,
      `ls${standardNum}_self_pass1`
    ];

    return variableNames.map(varName => {
      const variable = apiData.variables.find(v => v.variable === varName);
      return variable?.score === targetScore ? 1 : 0;
    });
  };

  const leadershipHeartQuestions = [
    { id: 1, question: "When leading through conflict, my goal is to accomplish our objective by any means necessary." },
    { id: 2, question: "I am at my best when I am driving others to deliver results." },
    { id: 3, question: "I am motivated by achieving measurable and attainable goals." },
    { id: 4, question: "I like the challenge of working in a high-performance leadership team." },
    { id: 5, question: "I believe great execution is primary." },
    { id: 6, question: "I am motivated to push my team to win." },
    { id: 7, question: "I have found success by outworking other people." },
    { id: 8, question: "I consider myself an action orientated manager who can drive objectives." },
    { id: 9, question: "I pride myself on superior execution." },
    { id: 10, question: "I like to win in a competitive environment." }
  ];

  const leadershipSoulQuestions = [
    { id: 1, question: "When leading through conflict, I follow a specific process to work through it." },
    { id: 2, question: "I am at my best when creating standardized processes that help others achieve their goals more easily." },
    { id: 3, question: "I believe leading an organization requires a unified set of operating principles." },
    { id: 4, question: "My most important responsibility is the long-term effective stewardship of organizational assets." },
    { id: 5, question: "I believe effective strategy is primary." },
    { id: 6, question: "I believe the best way to achieve effectiveness is through good policies." },
    { id: 7, question: "I believe strategic thinking is the main contributor of success." },
    { id: 8, question: "I intentionally develop others by educating them on what they need to know." },
    { id: 9, question: "I believe success is contingent on thinking smarter." },
    { id: 10, question: "I find great satisfaction in developing effective strategies for our organization." }
  ];

  const leadershipMindQuestions = [
    { id: 1, question: "When leading through conflict, I can envision how this particular circumstance fits within a larger story." },
    { id: 2, question: "I am at my best when casting a vision of where we should be heading." },
    { id: 3, question: "I believe core stories help us understand and communicate the values of an organization." },
    { id: 4, question: "I am always looking to place a particular project within the context of the larger purpose." },
    { id: 5, question: "I believe sharing inspirational stories should be an important part of a company's culture." },
    { id: 6, question: "I am motivated by creativity." },
    { id: 7, question: "I believe success is contingent on having a clear vision." },
    { id: 8, question: "My superpower is to see the \"why\" behind the \"what\" or the \"how.\"" },
    { id: 9, question: "I can weave a powerful narrative that unites people for a common purpose." },
    { id: 10, question: "I can usually see what lies ahead in the future of our organization." }
  ];

  const leadershipStrengthQuestions = [
    { id: 1, question: "When leading through conflict, I am attuned to the feelings everyone is experiencing." },
    { id: 2, question: "I am at my best when I feel more deeply connected to others." },
    { id: 3, question: "I want to build an emotionally intelligent organization." },
    { id: 4, question: "I believe relationships of trust are essential to create a successful organization." },
    { id: 5, question: "I believe developing a healthy culture is a very high value of my leadership." },
    { id: 6, question: "I am motivated to help my team connect more deeply with each other." },
    { id: 7, question: "I find fulfillment in developing a collaborative leadership culture." },
    { id: 8, question: "I develop others by first getting buy-in on our shared values." },
    { id: 9, question: "I enjoy building an empowering culture at work." },
    { id: 10, question: "I want our people to be fully engaged in the decision-making process." }
  ];

  useEffect(() => {
    const performanceObj = groupReportCtx?.averagePercentagesLeadershipSelf?.find((el) => el?.name == "Performance" )
    const strategiceObj = groupReportCtx?.averagePercentagesLeadershipSelf?.find((el) => el?.name == "Strategic" )
    const visionaryObj = groupReportCtx?.averagePercentagesLeadershipSelf?.find((el) => el?.name == "Visionary" )
    const collaborativeObj = groupReportCtx?.averagePercentagesLeadershipSelf?.find((el) => el?.name == "Collaborative" )

    const avgPercentSelf = [  strategiceObj?.avg, visionaryObj?.avg,   performanceObj?.avg,  collaborativeObj?.avg,  ]
    const selfLeadership = groupReportCtx?.averagePercentagesLeadershipSelf;
    // 4-way tie: first style in clockwise order (Performance) wins
    const maxAvgObj = isFullTie(selfLeadership?.map((el) => el?.avg))
      ? applyFullTieOrder(selfLeadership, (el) => el?.name, (el) => el?.avg)[0]
      : selfLeadership?.reduce((max, current) => {
        return current.avg > max.avg ? current : max;
      });
    setLeadershipSnapshotResults(avgPercentSelf);
    setLeadershipLabels(["strategic", "visionary", "performance", "collaborative"]);
    setMostSelecTedStarter(maxAvgObj?.name?.toLowerCase());
  }, [apiData, groupReportCtx?.averagePercentagesLeadershipSelf]);

  return (
    <div>
      <HeaderPreview apiData={apiData} />
      <div style={{
        padding: '16px 0px 0px',
        fontFamily: 'Helvetica, sans-serif',
        backgroundColor: '#ffffff'
      }}>
        <h2 style={{
          fontFamily: "Helvetica",
          fontWeight: 700,
          fontStyle: "normal",
          fontSize: "14px",
          lineHeight: "18px",
          letterSpacing: "0px",
          marginTop:"0px",
          textTransform: "uppercase",
          color: "#00577D",
          leadingTrim: "none",
        }}>LEADERSHIP STYLE OVERVIEW CHART</h2>

        <div style={{
          width: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          position: 'relative'
        }}>
          <div style={{transform: 'translateX(-20px)', flex: '0 0 240px', maxWidth: '300px', height: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'visible'}}>
            <PolarAreaChart
              originalData={leadershipSnapshotResults || []}
              labels={leadershipLabels || []}
              ch='strategic'
              position='center'
              selfChart={true}
            />
          </div>

          <div style={{ flex: '1 1 0', display: "flex", justifyContent: "center", alignItems: "flex-start", flexDirection: "column", transform: 'translateX(-40px)', minWidth: '242px' }}>
            <h4 style={{
              width: '100%',
              fontFamily: "Helvetica, sans-serif",
              fontWeight: 700,
              fontStyle: "normal",
              fontSize: "11px",
              lineHeight: "18px",
              letterSpacing: "0px",
              textTransform: "uppercase",
              color: "#000000",
              marginBottom: '10px',
              marginTop: 0,
            }}>
              STRONGEST CONFIRMATIONS AS A<br /> {mostSelecTedStarter?.toUpperCase()} LEADER
            </h4>
            {/* Dynamic Top 3 Points for Highest Quadrant */}
            {
              (() => {
                // Helper to get top 3 questions for the highest quadrant
                let questions = [];
                let targetScore = 0;
                if (mostSelecTedStarter?.includes("performance")) {
                  questions = leadershipHeartQuestions;
                  targetScore = 1;
                } else if (mostSelecTedStarter?.includes("strategic")) {
                  questions = leadershipSoulQuestions;
                  targetScore = 2;
                } else if (mostSelecTedStarter?.includes("visionary")) {
                  questions = leadershipMindQuestions;
                  targetScore = 3;
                } else if (mostSelecTedStarter?.includes("collaborative")) {
                  questions = leadershipStrengthQuestions;
                  targetScore = 4;
                }
                // For each question, sum the scores for the 4 variables for that question for this quadrant
                const scoredQuestions = questions.map(q => {
                  const scores = getScores(q.id, targetScore);
                  return { ...q, score: scores.reduce((a, b) => a + b, 0) };
                });
                scoredQuestions.sort((a, b) => b.score - a.score || a.id - b.id);
                const top3 = getTop3Questions()
                return (
                  <ol style={{
                    margin: '0',
                    listStyleType: 'decimal',
                    padding: "0px 0px 0px 16px"
                  }}>
                    {top3.length === 0 ? (
                      <li style={{ fontFamily: "Helvetica, sans-serif", fontWeight: 400, fontStyle: "normal", fontSize: "10px", lineHeight: "14px", letterSpacing: "0px", color: "#000000", marginBottom: "0px" }}>No data available.</li>
                    ) : (
                      top3.map((q, idx) => (
                        <li key={q.id} style={{ fontFamily: "Helvetica, sans-serif", fontWeight: 400, fontStyle: "normal", fontSize: "10px", lineHeight: "14px", letterSpacing: "0px", color: "#000000", marginBottom: "0px" }}>{q.question}</li>
                      ))
                    )}
                  </ol>
                );
              })()
            }
          </div>
        </div>
      </div>

      <h2 style={{
          fontFamily: "Helvetica",
          fontWeight: 700,
          fontStyle: "normal",
          fontSize: "14px",
          lineHeight: "18px",
          letterSpacing: "0px",
          textTransform: "uppercase",
          color: "#00577D",
          leadingTrim: "none",
          margin:"0px"
        }} >NOTES AND OBSERVATIONS:</h2>
      <FooterPreview pageNumber={pageNumber} />
    </div>
  )
}
